import { useEffect, useState } from "react";
import { api } from "../lib/config";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { SkipForward, PauseCircle, Repeat, Loader2, CalendarDays } from "lucide-react";

/**
 * RecurringQuickActions — iter251 (2026-08-22)
 *
 * Owner directive: surface Skip Next Service, Pause Service, and Change
 * Frequency directly on the Dashboard recurring booking card — NOT
 * hidden inside "Manage Service".  Only renders when the booking is
 * part of an active recurring schedule.  Preserves the existing
 * Reschedule / Add Extra Work / Manage Service / Cancel buttons above.
 */

const FREQ_OPTIONS = [
  { key: "weekly",   label: "Weekly" },
  { key: "biweekly", label: "Every 2 Weeks" },
  { key: "monthly",  label: "Monthly" },
];

// iter253 (2026-08-23) — timezone-safe helpers.  `toLocalYMD(date)` reads
// the browser's LOCAL year/month/day so we never shift a day when an
// evening timezone converts to UTC.  `formatLocalDate("YYYY-MM-DD")`
// renders the picker value back in the customer's local calendar for
// the "Service paused until …" preview.
function toLocalYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function formatLocalDate(ymd) {
  if (!ymd || typeof ymd !== "string") return "";
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return ymd;
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
}
function tomorrowLocalYMD() {
  const t = new Date();
  t.setDate(t.getDate() + 1);
  return toLocalYMD(t);
}

export default function RecurringQuickActions({ booking, cardBookingId, cardPropertyId, onRefresh }) {
  const [busy, setBusy] = useState(null);          // "skip" | "pause" | "freq"
  const [dialog, setDialog] = useState(null);      // { kind, resumeDate?, freq? }
  // iter253 — snapshot of the customer's current schedule so we can
  // pre-fill "already paused" state and surface the existing resume date
  // when the customer taps Pause Service a second time.
  const [scheduleSnapshot, setScheduleSnapshot] = useState(null);

  const currentFreq = scheduleSnapshot?.frequency || booking?.frequency || "";
  // iter252 — testids scope to the CARD the buttons are rendered on so
  // Playwright can target the row even when the same three buttons are
  // rendered on multiple cards (customer-level surfacing).
  const idSuffix = cardBookingId || booking?.booking_id || "recurring";

  const openPause = async () => {
    // Fetch the freshest schedule so a repeat Pause opens with the
    // existing resume_at pre-filled (owner directive iter253).
    let prefill = "";
    try {
      const { data } = await api.get("/me/recurring");
      setScheduleSnapshot(data?.schedule || null);
      prefill = data?.schedule?.resume_at || "";
    } catch { /* fall through with empty prefill */ }
    setDialog({ kind: "pause", resumeDate: prefill });
  };

  const openFreq = async () => {
    let curFreq = booking?.frequency || "";
    try {
      const { data } = await api.get("/me/recurring");
      setScheduleSnapshot(data?.schedule || null);
      curFreq = data?.schedule?.frequency || curFreq;
    } catch { /* keep booking freq */ }
    setDialog({ kind: "freq", freq: curFreq });
  };

  // iter256 — Every recurring action must scope to the property this
  // booking card belongs to.  The card sits on a specific booking, so
  // we always forward `booking_id` (backend resolves the property).
  // For customers with multiple properties this is what keeps a Skip
  // on Home from touching Rental (and vice versa).
  const scope = () => ({
    booking_id: cardBookingId || booking?.booking_id,
    property_id: cardPropertyId || booking?.property_id || null,
  });

  const doSkip = async () => {
    setBusy("skip");
    try {
      const r = await api.post("/me/recurring/skip-next", scope());
      const nextDate = r?.data?.next_date;
      const skippedDate = r?.data?.skipped_date;
      const fmt = (ymd) => {
        if (!ymd) return "";
        const [y, m, d] = ymd.split("-").map(Number);
        return new Date(y, m - 1, d).toLocaleDateString(undefined, {
          month: "long", day: "numeric", year: "numeric",
        });
      };
      if (nextDate) {
        toast.success(
          `Your ${fmt(skippedDate)} visit was skipped. Your next service is scheduled for ${fmt(nextDate)}.`,
        );
      } else {
        toast.success(`Skipped visit on ${skippedDate}`);
      }
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not skip next visit");
    } finally {
      setBusy(null);
      setDialog(null);
    }
  };

  const doPause = async (resumeDate) => {
    setBusy("pause");
    try {
      await api.post("/me/recurring/pause",
                     { resume_date: resumeDate || null, ...scope() });
      toast.success(
        resumeDate
          ? `Service paused until ${formatLocalDate(resumeDate)}. We'll automatically resume then.`
          : "Service paused. Tap Resume Now whenever you're ready.",
      );
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not pause service");
    } finally {
      setBusy(null);
      setDialog(null);
    }
  };

  const doChangeFreq = async (freq) => {
    setBusy("freq");
    try {
      await api.post("/me/recurring/change-frequency",
                     { service_frequency: freq, ...scope() });
      const label = FREQ_OPTIONS.find((o) => o.key === freq)?.label || freq;
      toast.success(`Frequency changed to ${label} — future visits only.`);
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change frequency");
    } finally {
      setBusy(null);
      setDialog(null);
    }
  };

  return (
    <>
      <div className="mt-2 flex flex-wrap gap-2" data-testid={`recurring-quick-actions-${idSuffix}`}>
        <Button size="sm" variant="outline"
                onClick={() => setDialog({ kind: "skip" })}
                disabled={busy !== null}
                className="rounded-full h-9 text-xs border-sky-300 text-sky-800 hover:bg-sky-50"
                data-testid={`quick-skip-${idSuffix}`}>
          {busy === "skip"
            ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
            : <SkipForward className="w-3.5 h-3.5 mr-1" />}
          Skip Next Service
        </Button>
        <Button size="sm" variant="outline"
                onClick={openPause}
                disabled={busy !== null}
                className="rounded-full h-9 text-xs border-amber-300 text-amber-800 hover:bg-amber-50"
                data-testid={`quick-pause-${idSuffix}`}>
          {busy === "pause"
            ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
            : <PauseCircle className="w-3.5 h-3.5 mr-1" />}
          Pause Service
        </Button>
        <Button size="sm" variant="outline"
                onClick={openFreq}
                disabled={busy !== null}
                className="rounded-full h-9 text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                data-testid={`quick-freq-${idSuffix}`}>
          {busy === "freq"
            ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
            : <Repeat className="w-3.5 h-3.5 mr-1" />}
          Change Frequency
        </Button>
      </div>

      <AlertDialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <AlertDialogContent className="max-w-md">
          {dialog?.kind === "skip" && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Skip your next visit?</AlertDialogTitle>
                <AlertDialogDescription className="text-[13px] leading-relaxed">
                  We&apos;ll move your next scheduled visit forward by one
                  occurrence. Your recurring schedule stays active.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={busy !== null} data-testid="quick-skip-cancel">Keep visit</AlertDialogCancel>
                <AlertDialogAction onClick={doSkip} disabled={busy !== null}
                                   data-testid="quick-skip-confirm">
                  {busy === "skip" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                  Yes, skip once
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
          {dialog?.kind === "pause" && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Pause your recurring service?</AlertDialogTitle>
                <AlertDialogDescription className="text-[13px] leading-relaxed">
                  Choose when you want your service to resume. Your saved
                  payment method and service history stay intact — we&apos;ll
                  automatically restart your recurring visits on the date
                  you pick.
                </AlertDialogDescription>
              </AlertDialogHeader>

              {scheduleSnapshot?.status === "paused" && scheduleSnapshot?.resume_at && (
                <div className="mt-1 mb-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] font-semibold text-amber-900"
                     data-testid="quick-pause-existing">
                  Currently paused until <b>{formatLocalDate(scheduleSnapshot.resume_at)}</b>. Pick a
                  new date below to change it.
                </div>
              )}

              <div className="my-3">
                <label className="text-[11px] uppercase tracking-[0.2em] font-extrabold text-gray-500 flex items-center gap-1.5">
                  <CalendarDays className="w-3.5 h-3.5 text-gray-500" />
                  Resume date
                </label>
                <input type="date"
                       min={tomorrowLocalYMD()}
                       value={dialog.resumeDate || ""}
                       onChange={(e) => setDialog((s) => ({ ...s, resumeDate: e.target.value }))}
                       className="mt-1 w-full h-12 px-3 rounded-lg border-2 border-amber-300 text-base font-bold text-gray-900 bg-white focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                       data-testid="quick-pause-resume-date"
                       required />
                <p className="mt-2 text-[11px] font-semibold text-gray-500">
                  Full calendar — pick any future date, including months and years ahead.
                </p>
              </div>

              {dialog.resumeDate && (
                <div className="mb-2 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2.5 text-[13px] font-bold text-emerald-900"
                     data-testid="quick-pause-preview">
                  Service paused until <b>{formatLocalDate(dialog.resumeDate)}</b>
                </div>
              )}

              <AlertDialogFooter>
                <AlertDialogCancel disabled={busy !== null} data-testid="quick-pause-cancel">Not now</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => doPause(dialog.resumeDate)}
                  disabled={busy !== null || !dialog.resumeDate || dialog.resumeDate < tomorrowLocalYMD()}
                  className="bg-amber-700 hover:bg-amber-800 disabled:bg-amber-300 disabled:cursor-not-allowed"
                  data-testid="quick-pause-confirm"
                >
                  {busy === "pause" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                  Yes, pause service
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
          {dialog?.kind === "freq" && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>Change service frequency</AlertDialogTitle>
                <AlertDialogDescription className="text-[13px] leading-relaxed">
                  Only future visits are affected. Completed visits and paid
                  invoices stay exactly as they are. Your locked per-visit
                  price stays the same.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="my-3 grid grid-cols-3 gap-2">
                {FREQ_OPTIONS.map((o) => (
                  <button key={o.key} type="button"
                          onClick={() => setDialog((s) => ({ ...s, freq: o.key }))}
                          className={`rounded-xl border-2 p-3 text-sm font-extrabold transition ${
                            dialog.freq === o.key
                              ? "border-emerald-600 bg-emerald-50 text-emerald-900"
                              : "border-gray-200 text-gray-700 hover:border-gray-300"
                          }`}
                          data-testid={`quick-freq-opt-${o.key}`}>
                    {o.label}
                    {currentFreq === o.key && (
                      <div className="mt-0.5 text-[10px] font-bold text-gray-500 uppercase tracking-wider">Current</div>
                    )}
                  </button>
                ))}
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={busy !== null} data-testid="quick-freq-cancel">Keep current</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => doChangeFreq(dialog.freq)}
                  disabled={busy !== null || !dialog.freq || dialog.freq === currentFreq}
                  data-testid="quick-freq-confirm"
                >
                  {busy === "freq" ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                  Apply new frequency
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
